"use client";

import React from "react";
import { PhoneCall, CalendarCheck, ShieldCheck, Clock, CircleCheck as CheckCircle2, Flame, Droplets, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const quickFeatures = [{
  id: "1",
  label: "24/7 Live Emergency Dispatch",
  icon: Clock
}, {
  id: "2",
  label: "Licensed & Insured Master Plumbers",
  icon: ShieldCheck
}, {
  id: "3",
  label: "Upfront Pricing With No Hidden Fees",
  icon: CheckCircle2
}];
const servicePills = [{
  id: "s1",
  name: "Emergency Leak Repair",
  icon: Droplets
}, {
  id: "s2",
  name: "Water Heater Service",
  icon: Flame
}, {
  id: "s3",
  name: "Drain & Sewer Clearing",
  icon: Wrench
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-8 pb-16 md:pt-14 md:pb-24 border-b border-border">
      {/* Background Decorative Subtle Tint & Grids */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(215_85%_22%_/_0.06),transparent_50%)] pointer-events-none" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none transform translate-x-1/3 -translate-y-1/3" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Main Content Column */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-7 flex flex-col items-start">
            {/* 24/7 Status Badge */}
            <motion.div variants={itemVariants} className="mb-5">
              <Badge variant="secondary" className="rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-semibold bg-secondary text-secondary-foreground border border-border shadow-xs flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-destructive" />
                </span>
                24/7 Emergency Dispatch Available in Jonesboro, GA
              </Badge>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 variants={itemVariants} className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15]">
              Professional Plumbing Services in{" "}
              <span className="text-primary underline decoration-accent decoration-4 underline-offset-6">
                Jonesboro, GA
              </span>
            </motion.h1>

            {/* Informative Lead Paragraph */}
            <motion.p variants={itemVariants} className="mt-6 text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              Fast, reliable residential plumbing for burst pipes, hidden water
              leaks, clogged drains, water heater failures, overflowing toilets,
              and sewer line backups. Available around the clock for immediate local
              dispatch.
            </motion.p>

            {/* Dual CTA Cluster */}
            <motion.div variants={itemVariants} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full sm:w-auto">
              {/* Primary Tel CTA */}
              <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground font-bold shadow-md hover:bg-accent/90 h-13 px-8 text-base transition-all hover:scale-[1.02] active:scale-[0.98]">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2.5" aria-label="Call (470) 847-9076 for immediate plumbing emergency service">
                  <PhoneCall className="size-5 shrink-0 animate-bounce" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              {/* Secondary Request Service CTA */}
              <Button asChild variant="outline" size="lg" className="rounded-full border-border bg-card text-card-foreground font-semibold shadow-xs hover:bg-secondary h-13 px-7 text-base transition-all">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <CalendarCheck className="size-4 shrink-0 text-primary" />
                  <span>Request Service</span>
                </Link>
              </Button>
            </motion.div>

            {/* Key Trust Signals */}
            <motion.div variants={itemVariants} className="mt-10 pt-6 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
              {quickFeatures.map((feat, index) => {
              const IconComponent = feat.icon;
              return <div key={feat.id} data-index={index} className="flex items-center gap-2.5">
                    <div className="size-7 rounded-full bg-secondary flex items-center justify-center shrink-0">
                      <IconComponent className="size-4 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-muted-foreground leading-snug">
                      {feat.label}
                    </span>
                  </div>;
            })}
            </motion.div>
          </motion.div>

          {/* Right Visual Card Column */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl bg-card border border-border p-3 sm:p-4 shadow-xl overflow-hidden">
              {/* Representative Service Image */}
              <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service_van.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service_van.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service_van.webp-wvc-sizes" alt="Jonesboro Emergency Plumbing technician service truck ready for 24/7 dispatch" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />

                {/* Floating On-Image Status Pill */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between rounded-lg bg-background/95 backdrop-blur-xs p-3 border border-border shadow-sm">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      Average Arrival Time
                    </p>
                    <p className="text-sm font-bold text-foreground">
                      45-60 Minutes in Clayton County
                    </p>
                  </div>
                  <div className="size-8 rounded-full bg-accent/20 flex items-center justify-center text-accent-foreground font-bold text-xs">
                    GA
                  </div>
                </div>
              </div>

              {/* Service Badges Row below Image */}
              <div className="mt-4 pt-1 flex flex-wrap gap-2">
                {servicePills.map((service, index) => {
                const SIcon = service.icon;
                return <div key={service.id} data-index={index} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-medium border border-border/60">
                      <SIcon className="size-3.5 text-primary" />
                      <span>{service.name}</span>
                    </div>;
              })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}